/**
 * Docs site helpers.
 *
 * Customer-facing docs live on the marketing site (Starlight), NOT on the app
 * domain. The app is `app.aelo.cloud`; docs are served from:
 *
 *   https://aelo.cloud/{lang}/docs/user-guide/{page}/
 *
 * Astro is configured with `prefixDefaultLocale: true` and
 * `trailingSlash: 'always'` (see apps/website/astro.config.mjs), so every
 * locale — including English — is prefixed and every URL ends with a slash.
 *
 * Always build docs links through `getDocsUrl()` so they point at the correct
 * domain/locale and never accidentally stay on the app domain (a relative
 * `/docs/...` href resolves against the app and 404s).
 */

const DOCS_BASE_URL = 'https://aelo.cloud';

/** Locales the website docs are published in (mirrors astro i18n config). */
const DOCS_LANGS = ['en', 'ru', 'uk', 'es'] as const;

/**
 * Локаль документации из локали интерфейса.
 *
 * Экспортируется, потому что не только ссылки строятся по этому правилу:
 * поп-ап релиза выбирает по нему язык своего текста, и вторая копия правила
 * разошлась бы с этой при первом же изменении набора локалей.
 */
export function normalizeDocsLang(lang?: string): string {
  const base = (lang ?? 'en').split('-')[0] ?? 'en';
  return (DOCS_LANGS as readonly string[]).includes(base) ? base : 'en';
}

/**
 * Build an absolute URL to a docs page.
 *
 * @param slug Docs slug without surrounding slashes, e.g.
 *   `'user-guide/conversations'`. Omit (or pass `undefined`) for the docs home.
 * @param lang Current UI language (e.g. from `i18n.language`); falls back to `en`.
 */
export function getDocsUrl(slug?: string, lang?: string): string {
  const locale = normalizeDocsLang(lang);
  const cleaned = slug?.replace(/^\/+|\/+$/g, '');
  const path = cleaned ? `docs/${cleaned}` : 'docs';
  return `${DOCS_BASE_URL}/${locale}/${path}/`;
}

/**
 * Map an in-app route to the most relevant docs slug.
 *
 * Returns `undefined` when no specific section matches — callers should fall
 * back to the docs home. Ordering matters: first matching prefix wins.
 */
export function getContextualDocsSlug(pathname: string): string | undefined {
  const p = pathname.replace(/\/+$/, '') || '/';

  const map: Array<[RegExp, string]> = [
    [/^\/dashboard/, 'user-guide/dashboard'],
    [/^\/records/, 'user-guide/conversations'],
    [/^\/analysis-setup\/categories/, 'user-guide/evaluation-criteria'],
    [/^\/analysis-setup\/call-criteria/, 'user-guide/call-criteria'],
    [/^\/analysis-setup\/scripts/, 'user-guide/scripts'],
    [/^\/analysis-setup\/sales-methodology/, 'user-guide/deals'],
    [/^\/alerts/, 'user-guide/alerts'],
    [/^\/reports/, 'user-guide/reports'],
    [/^\/analytics/, 'user-guide/reports'],
    [/^\/review-queue/, 'user-guide/quality'],
    [/^\/coaching/, 'user-guide/quality'],
    [/^\/users/, 'user-guide/team'],
    [/^\/billing/, 'user-guide/package-matrix'],
    [/^\/settings/, 'user-guide/settings'],
    [/^\/admin/, 'user-guide/settings'],
    [/^\/projects/, 'user-guide/getting-started'],
    [/^\/knowledge-base/, 'user-guide/getting-started'],
  ];

  for (const [re, slug] of map) {
    if (re.test(p)) return slug;
  }
  return undefined;
}
